import React, { useEffect, useState, useRef } from 'react';
import styles from './KioskPage.module.css';
import { mountKioskOfflineIndicator } from './mountKioskOfflineIndicator';
// Firebase removed - using local data loading
import { loadUpdates } from './utils/kioskUtils';
import { loadCoreData } from '../calendar/calendar.js';
import { loadRambamData, loadTanyaData } from '../utils/dataLogic';
import { getCurrentItemIndex, getDate, reloadWithNextSaturday, formatTime } from '../utils/dateUtils';
import { useHiddenSettingsClickHandler, initZoom } from '../settings/hiddenSettings';
import Overlay from '../overlay/Overlay.js';
import GivebutterWidget from '../GivebutterWidget';
import { logger } from '../utils/kioskLogger';
import { safeReload } from '../utils/safeReload';
import { bidiText } from '../utils/bidiText';
import DateTimeHeader from './components/DateTimeHeader';
import ZmanimSection from './components/ZmanimSection';
import DailyStudiesSection from './components/DailyStudiesSection';
import ScheduleSection from './components/ScheduleSection';
import NewsSection from './components/NewsSection';
import SefirahSection from './components/SefirahSection';
import NoticesSection from './components/NoticesSection';
import DonationSection from './components/DonationSection';
import PrintScheduleSection from './components/PrintScheduleSection';
import ScheduleValue from '../components/ScheduleValue';

const HEBREW_IN_STRING = /[\u0590-\u05FF]/;
/** Schedule values with Hebrew need RTL base direction; times stay LTR (matches scheduleRenderer data-hebrew logic). */
function scheduleValueDirection(value) {
  return HEBREW_IN_STRING.test(value || '') ? 'rtl' : 'ltr';
}

function KioskPage() {
  const printParam = new URLSearchParams(window.location.search).get('print');
  const isPrintPreview = printParam === '' || printParam === 'true';
  const [scheduleData, setScheduleData] = useState({});
  const [upcomingData, setUpcomingData] = useState({});
  const [printSchedule, setPrintSchedule] = useState({});
  const [currentIndex, setCurrentIndex] = useState(null);
  const [zmanimData, setZmanim] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [dailyStudiesData, setDailyStudiesData] = useState([]);
  const [weeklyNotices, setWeeklyNotices] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hebrewDate, setHebrewDate] = useState('');
  const [parsha, setParsha] = useState('');
  const [chazak, setChazak] = useState(false);
  const [specialDay, setSpecialDay] = useState('');
  const [announcements, setAnnouncements] = useState('');
  const [displayOptions, setDisplayOptions] = useState({});
  const [sefirahContent, setSefirahContent] = useState([]);
  const [threeKapitelsContent, setThreeKapitelsContent] = useState([]);

  const buildNumberRef = useRef(null);
  const errorCountRef = useRef(0);
  const serviceScheduleRef = useRef(null);
  const isFetchingRef = useRef(false);
  const prevScheduleDataRef = useRef(scheduleData);

  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');

  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState("current");

  const { handleElementClick, HiddenSettingsUI } = useHiddenSettingsClickHandler();

  const showOverlay = (url) => {
    setIframeUrl(url);
    setOverlayVisible(true);
  };

  const closeOverlay = () => {
    setOverlayVisible(false);
    setIframeUrl('');
  };

  function printCss() {
    // The designed preview uses bundled, scoped styles. Keep the legacy
    // stylesheet for printing the regular kiosk and for the separate /print page.
    if (isPrintPreview) return null;

    const printLink = document.createElement("link");
    printLink.rel = "stylesheet";
    printLink.href = "/print.css";
    printLink.media = "print";

    document.head.appendChild(printLink);
    return printLink;
  }

  useEffect(() => {
    return mountKioskOfflineIndicator();
  }, []);

  useEffect(() => {
    // Initialize logger
    logger.init();

    // Set isMobile based on window width (client-side only)
    if (typeof window !== 'undefined') {
      setIsMobile(window.innerWidth < 768);
    }

    initZoom();
    const date = getDate();
    console.log('Using Date/Time', date);
    logger.info('Kiosk page initialized', { date: date.toISOString() });
    const coreData = loadCoreData(date);
    setHebrewDate(coreData.hebrewDate);
    setSpecialDay(coreData.specialDay);
    
    const dailyStudies = loadRambamData(date);
    loadTanyaData(date, dailyStudies).then(setDailyStudiesData);

    const clockTimer = setInterval(() => {
      setCurrentTime(getDate());
    }, 1000);

    const updatesInterval = setInterval(dataLoaderFX, 30000);

    function dataLoaderFX() {
      // Prevent concurrent fetches
      if (isFetchingRef.current) {
        logger.warn('Skipping fetch - previous fetch still in progress');
        return;
      }
      
      isFetchingRef.current = true;
      let dataLoader = loadUpdates(
        setScheduleData,
        setPrintSchedule,
        setUpcomingData,
        setNewsData,
        setZmanim,
        setParsha,
        setWeeklyNotices,
        setAnnouncements,
        setDisplayOptions,
        setSefirahContent,
        setThreeKapitelsContent,
        buildNumberRef,
        errorCountRef
        // storage parameter removed - using local data loading
      );
      dataLoader.then((data) => {
        updateCurrentItem(data.visibleSchedule);
        if (data.chazak !== undefined) {
          setChazak(data.chazak);
        }
      }).catch((error) => {
        logger.error('Unexpected error in dataLoaderFX', { error: error.message });
      }).finally(() => {
        isFetchingRef.current = false;
      });
    }

    dataLoaderFX();

    function updateCurrentItem(data) {
      if (!data || !data.schedule) {
        if (data && !data.schedule) {
          logger.warn('updateCurrentItem called with data but no schedule', {
            dataKeys: Object.keys(data || {}),
            isOnline: navigator.onLine
          });
        }
        return;
      }
      const baseDate = getDate();
      
      // Get today's date in YYYY-MM-DD format
      const today = new Date(baseDate);
      const todayStr = today.toISOString().split('T')[0]; // "YYYY-MM-DD"
      
      // Check if all items have future dates
      const allDates = data.schedule
        .filter(item => item.date)
        .map(item => item.date);
      
      // If all items have dates and they're all for a future date, highlight the first item
      if (allDates.length === data.schedule.length && allDates.length > 0) {
        const allFutureDates = allDates.every(date => date > todayStr);
        if (allFutureDates) {
          console.log("All items have future dates. Forcing highlight of first item.");
          setCurrentIndex(0);
          return;
        }
      }
      
      const idx = getCurrentItemIndex(data.schedule, baseDate);
      setCurrentIndex(idx);
    }

    const now = new Date();
    let targetTime = new Date();
    targetTime.setHours(23, 59, 59, 999);
    if (now >= targetTime) {
      targetTime.setDate(targetTime.getDate() + 1);
    }
    const delay = targetTime - now;
    const endOfDayTimer = setTimeout(() => {
      console.log('Refreshing at end of the day');
      safeReload({ reason: 'end_of_day', needNewBuild: false });
    }, delay);

    let printLink = printCss();

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(clockTimer);
      clearInterval(updatesInterval);
      clearTimeout(endOfDayTimer);
      if (printLink) document.head.removeChild(printLink);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Monitor scheduleData changes to detect when it becomes empty
  useEffect(() => {
    const hasSchedule = scheduleData?.schedule && scheduleData.schedule.length > 0;
    const prevHasSchedule = prevScheduleDataRef.current?.schedule && prevScheduleDataRef.current.schedule.length > 0;
    
    if (prevHasSchedule && !hasSchedule) {
      // Schedule data became empty
      logger.warn('Schedule data became empty', {
        previousScheduleCount: prevScheduleDataRef.current?.schedule?.length || 0,
        currentScheduleCount: scheduleData?.schedule?.length || 0,
        scheduleDataKeys: Object.keys(scheduleData || {}),
        isOnline: navigator.onLine
      });
    } else if (!prevHasSchedule && hasSchedule) {
      // Schedule data was restored
      logger.info('Schedule data restored', {
        scheduleCount: scheduleData.schedule.length,
        isOnline: navigator.onLine
      });
    }
    
    prevScheduleDataRef.current = scheduleData;
  }, [scheduleData]);

  // Define panel components using the extracted components
  const leftPanel = (
    <div className={`${styles.leftPanel} no-print`}>
      {/* Left panel content (Zmanim & Daily Studies) */}
      {displayOptions.showZmanim !== false && (
        <section className={styles.zmanimFeed}>
          <h2 className={styles.hebrewHeading}>זמנים</h2>
          <div className={styles.zmanimContent}>
            {zmanimData
              .filter(zman => {
                // Filter out hidden items
                if (zman.hideItem) return false;
                // Filter by selected zmanim if specified
                if (displayOptions.selectedZmanim && displayOptions.selectedZmanim.length > 0) {
                  return displayOptions.selectedZmanim.includes(zman.zman);
                }
                // If no selection specified, show all
                return true;
              })
              .map((zman, index) => (
                <div key={index} className={styles.zmanimItem}>
                  <strong className={styles.zmanimTitle}>{zman.title}</strong>
                  <span className={styles.zmanimValue} suppressHydrationWarning>{formatTime(zman.time)}</span>
                </div>
              ))}
            <div style={{ fontSize: '15px', textAlign: 'center', marginTop: '5px', direction: 'ltr' }}>
              * Zmanim are never 100% exact.
            </div>
          </div>
        </section>
      )}
      {displayOptions.showDailyStudies !== false && (
        <section className={styles.dailyStudiesFeed}>
          <h2>Daily Studies</h2>
          <div className={styles.dailyStudiesContent}>
            {dailyStudiesData
              .filter(study => {
                // Map study titles to keys
                const studyKeyMap = {
                  'פרק אחד': 'onePerek',
                  'שלושה פרקים': 'threePerokim',
                  'תניא': 'tanya'
                };
                const studyKey = studyKeyMap[study.title];
                
                // Filter by selected studies if specified
                if (displayOptions.selectedDailyStudies && displayOptions.selectedDailyStudies.length > 0) {
                  return studyKey && displayOptions.selectedDailyStudies.includes(studyKey);
                }
                // If no selection specified, show all
                return true;
              })
              .map((study, index) => (
                <p key={index}>
                  <b>{study.title}</b> - {study.content}
                </p>
              ))}
          </div>
        </section>
      )}
    </div>
  );

  const centerPanel = (
    <div className={`${styles.centerPanel} no-print`}>
      {displayOptions.showServiceSchedule !== false && scheduleData && scheduleData.schedule && scheduleData.schedule.length > 0 && (
        <section className={styles.serviceSchedule} ref={serviceScheduleRef}>
          <div className={styles.scheduleSection}>
            <h2 className={styles.hebrewHeading}>{scheduleData.sectionTitle}</h2>
            <div className={styles.scheduleContent}>
              {scheduleData.schedule
                .filter(item => !item.hideItem && item.display !== 'print_only' && item.display !== 'none')
                .map((item, idx) => {
                  // Debug information to help understand the highlighting logic
                  // console.log(`Item ${idx}: ${item.title} - Current Index: ${currentIndex}`);
                  
                  const itemClass = idx === currentIndex ? `${styles.scheduleItem} ${styles.current}` : styles.scheduleItem;
                  const valueStr = item.value || '';
                  return (
                    <div key={idx} className={itemClass}>
                      <div className={styles.scheduleTitleContainer}>
                        <strong className={styles.scheduleTitle}>{bidiText(item.title)}</strong>
                        {item.byline && (
                          <div className={styles.scheduleByline}>
                            {bidiText(item.byline)}
                          </div>
                        )}
                      </div>
                      <ScheduleValue
                        className={styles.scheduleValue}
                        value={valueStr}
                        fontSize={item.fontSize}
                        isCurrent={idx === currentIndex}
                        style={{
                          direction: scheduleValueDirection(valueStr),
                        }}
                        suppressHydrationWarning
                      >
                        {bidiText(valueStr)}
                      </ScheduleValue>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {displayOptions.showUpcomingSchedule !== false && upcomingData && upcomingData.schedule && upcomingData.schedule.length > 0 && (
        <section className={styles.upcomingSchedule}>
          <div className={styles.scheduleSection}>
            <h2 className={styles.hebrewHeading}>{upcomingData.sectionTitle}</h2>
            <div className={styles.scheduleContent}>
              {upcomingData.schedule
                .filter(item => !item.hideItem && item.display !== 'print_only' && item.display !== 'none')
                .map((item, idx) => {
                  const valueStr = item.value || '';
                  return (
                    <div key={idx} className={styles.scheduleItem}>
                      <div className={styles.scheduleTitleContainer}>
                        <strong className={styles.scheduleTitle}>{bidiText(item.title)}</strong>
                        {item.byline && (
                          <div className={styles.scheduleByline}>
                            {bidiText(item.byline)}
                          </div>
                        )}
                      </div>
                      <ScheduleValue
                        className={styles.scheduleValue}
                        value={valueStr}
                        fontSize={item.fontSize}
                        style={{
                          direction: scheduleValueDirection(valueStr),
                        }}
                        suppressHydrationWarning
                      >
                        {bidiText(valueStr)}
                      </ScheduleValue>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}
    </div>
  );

  const rightPanel = (
    <div className={`${styles.rightPanel} no-print`}>
      {displayOptions.showSefirasHaomer && sefirahContent && sefirahContent.length > 0 ? (
        <section className={styles.newsFeed}>
          <h2>Sefiras Ha'omer</h2>
          <div className={styles.newsContent} style={{ textAlign: 'center', direction: 'rtl' }}>
            {sefirahContent.map((line, index) => (
              <p key={index} style={{ fontSize: index === 0 ? '1.5em' : '1.2em', fontWeight: index === 0 ? 'bold' : 'normal' }}>
                {line}
              </p>
            ))}
          </div>
        </section>
      ) : displayOptions.showThreeKapitels && threeKapitelsContent && threeKapitelsContent.length > 0 ? (
        <section className={styles.newsFeed}>
          <h2>שלושה קאַפּיטלעך</h2>
          <div className={styles.newsContent} style={{ textAlign: 'center', direction: 'rtl' }}>
            {threeKapitelsContent.map((line, index) => {
              const isHebrew = HEBREW_IN_STRING.test(line);
              const isPrimary = threeKapitelsContent.length <= 2 && index === 0;
              return (
                <p
                  key={index}
                  style={{
                    fontSize: isPrimary ? '1.5em' : '1.15em',
                    fontWeight: isPrimary ? 'bold' : 'normal',
                    direction: isHebrew ? 'rtl' : 'ltr',
                  }}
                >
                  {line}
                </p>
              );
            })}
          </div>
        </section>
      ) : displayOptions.showCommunityUpdates !== false && (
        <section className={styles.newsFeed}>
          <h2>Community Updates</h2>
          <div className={styles.newsContent}>
            {newsData.map((news, index) => (
              <p key={index}>{news}</p>
            ))}
          </div>
          {displayOptions.showProgressBar !== false && (
            <GivebutterWidget 
              widgetId="p7G1Op" 
              accountId="XM3IcCYYb48Jr18u" 
              page="other"
            />
          )}
        </section>
      )}
      {displayOptions.showNotices !== false && (
        <section className={styles.weeklyNotices}>
          <h2 className={styles.heading}>Notices</h2>
          <div className={styles.noticesContent}>
            {weeklyNotices.map((notice, index) => (
              <li dir="rtl" key={index}>{bidiText(notice)}</li>
            ))}
          </div>
        </section>
      )}
      {displayOptions.showDonation !== false && (
        <section className={styles.donation}>
          <h2>Support BLY</h2>
          <div className={styles.qrCodeContainer}>
            <img src="/donate-website-qr.png" alt="QR Code for Website" className={styles.qrCode} />
            <img src="/donate-zelle-qr.png" alt="QR Code for Zelle" className={styles.qrCode} />
          </div>
        </section>
      )}
      {displayOptions.showCustomHtml && displayOptions.customHtmlContent && (
        <section className={styles.customHtmlBox}>
          <div 
            className={styles.customHtmlContent}
            dangerouslySetInnerHTML={{ __html: displayOptions.customHtmlContent }}
          />
        </section>
      )}
    </div>
  );

  return (
    <div className={`App ${styles.kioskPage}${isPrintPreview ? ` ${styles.printPreview}` : ''}`}>
      {isOverlayVisible && <Overlay url={iframeUrl} onClose={closeOverlay} />}
      <HiddenSettingsUI />
      
      {/* Header remains the same; note that on mobile the time is hidden via CSS */}
      <header className={`${styles.dateTimeBar} no-print`}>
        <div className={styles.hebrewDate} onClick={(e) => { e.preventDefault(); showOverlay('https://videos.jem.tv/lt-archive'); }}>{hebrewDate}</div>
        <div className={styles.currentTime} onClick={handleElementClick} suppressHydrationWarning>
          {formatTime(currentTime, 'hh:mm:ss a')}
        </div>
        <div className={styles.parsha} onClick={(e) => { e.preventDefault(); showOverlay('https://videos.jem.tv/lt-archive'); }}>
          {parsha}{chazak ? ' - חזק' : ''}
        </div>
      </header>

      {isMobile ? (
        <div className={`${styles.mobileContainer} no-print`}>
          <div className={styles.mobileTabs}>
            <button
              className={activeTab === "zmanim" ? styles.active : ""}
              onClick={() => setActiveTab("zmanim")}
            >
              Zmanim
            </button>
            <button
              className={activeTab === "current" ? styles.active : ""}
              onClick={() => setActiveTab("current")}
            >
              Current
            </button>
            <button
              className={activeTab === "updates" ? styles.active : ""}
              onClick={() => setActiveTab("updates")}
            >
              Updates
            </button>
          </div>
          <div className={styles.mobileContent}>
            {activeTab === "zmanim" && leftPanel}
            {activeTab === "current" && centerPanel}
            {activeTab === "updates" && rightPanel}
          </div>
        </div>
      ) : (
        <div className={`${styles.contentContainer} no-print`}>
          {leftPanel}
          {centerPanel}
          {rightPanel}
        </div>
      )}

      <PrintScheduleSection 
        printSchedule={printSchedule} 
        announcements={announcements} 
        weeklyNotices={weeklyNotices} 
        reloadWithNextSaturday={reloadWithNextSaturday}
        isPrintPreview={isPrintPreview}
      />
    </div>
  );
}

export default KioskPage;
